/**
 * The price and stock cells, where each "no" is rendered as itself.
 *
 * A dash would be shorter, and it is exactly what this screen must not do:
 * "—" in a price column reads the same whether the company does not sell the
 * article or nobody priced it, and those are two different phone calls.
 */

import { useTranslation } from "react-i18next";
import { money, quantity } from "@hardauto/core-i18n";
import { Badge } from "@hardauto/core-ui";

import type { ConsultationRow, PriceState, StockState } from "../../api";
import { withTax } from "../../consultation";
import { NAMESPACE } from "../../i18n";

export function PriceStateBadge({ state }: { state: Exclude<PriceState, "priced"> }) {
  const { t } = useTranslation(NAMESPACE);
  return (
    <Badge tone={state === "missing" ? "warning" : "neutral"}>
      {t(`consultation.price.${state}`)}
    </Badge>
  );
}

export function StockStateBadge({ state }: { state: Exclude<StockState, "available"> }) {
  const { t } = useTranslation(NAMESPACE);
  const tone = state === "zero" ? "danger" : "neutral";
  return <Badge tone={tone}>{t(`consultation.stock.${state}`)}</Badge>;
}

export function PriceCell({
  row,
  locale,
  taxRatePct,
  onOpen,
  onPrefetch,
}: {
  row: ConsultationRow;
  locale: string;
  /** When given, the price is shown with VAT added; when not, as it comes. */
  taxRatePct?: string | undefined;
  /** When given, the price opens the list of tariffs. */
  onOpen?: (() => void) | undefined;
  /** Called as the pointer or focus arrives, so what the click opens can be loading already. */
  onPrefetch?: (() => void) | undefined;
}) {
  const { t } = useTranslation(NAMESPACE);
  if (row.price_state !== "priced" || row.unit_price === null) {
    return <PriceStateBadge state={row.price_state === "priced" ? "missing" : row.price_state} />;
  }
  const shown = taxRatePct === undefined ? row.unit_price : withTax(row.unit_price, taxRatePct);
  if (!onOpen) {
    return <span className="hardauto-num hardauto-num--strong">{money(shown, locale)}</span>;
  }
  return (
    <button
      type="button"
      className="hardauto-link-button hardauto-num hardauto-num--strong"
      title={t("consultation.priceDialog.open")}
      onClick={onOpen}
      onMouseEnter={onPrefetch}
      onFocus={onPrefetch}
      onTouchStart={onPrefetch}
    >
      {money(shown, locale)}
    </button>
  );
}

export function StockCell({
  row,
  locale,
  onOpen,
  onPrefetch,
}: {
  row: ConsultationRow;
  locale: string;
  /** When given, the quantity opens the stock by depot. */
  onOpen?: (() => void) | undefined;
  /** Called as the pointer or focus arrives, so what the click opens can be loading already. */
  onPrefetch?: (() => void) | undefined;
}) {
  const { t } = useTranslation(NAMESPACE);
  if (row.stock_state !== "available" || row.available === null) {
    return (
      <StockStateBadge state={row.stock_state === "available" ? "unknown" : row.stock_state} />
    );
  }
  return (
    <div className="hardauto-cell__stack hardauto-cell__stack--end">
      {onOpen ? (
        <button
          type="button"
          className="hardauto-link-button hardauto-num hardauto-num--strong"
          title={t("consultation.stockDialog.open")}
          onClick={onOpen}
          onMouseEnter={onPrefetch}
          onFocus={onPrefetch}
          onTouchStart={onPrefetch}
        >
          {quantity(row.available, locale)} {row.sales_unit}
        </button>
      ) : (
        <span className="hardauto-num hardauto-num--strong">
          {quantity(row.available, locale)} {row.sales_unit}
        </span>
      )}
      <span className="hardauto-cell__muted">
        {t("consultation.depotCount", { count: row.depots })}
      </span>
    </div>
  );
}
